import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Form, Input, Button, Card, Typography, App, Space, Divider, Alert, Steps } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { trpc } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { SERVICE_TITLE } from '@shared/config/service-config';
import { buildClientRedirectUrl, resolveCurrentClientType } from '../lib/clientType';
import { SEOHead } from '../components/SEOHead';
import { getMutationPendingState } from '../utils/trpcMutation';

const { Title, Text } = Typography;

interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

interface AuthUser {
  id: string;
  email: string;
  name: string | null;
  role?: string;
  companyId?: string;
}

interface LoginVerificationPayload {
  maskedEmail: string;
  expiresAt: string;
  resendAvailableAt: string;
}

interface LoginSuccessResponse {
  verificationRequired?: false;
  user: AuthUser;
  tokens: AuthTokens;
}

interface LoginChallengeResponse {
  verificationRequired: true;
  user: AuthUser;
  verification: LoginVerificationPayload;
}

interface LoginRedirectResponse {
  redirectRequired: true;
  targetClientType: string;
}

interface LoginForm {
  email: string;
  password: string;
}

interface PendingVerificationState {
  email: string;
  maskedEmail: string;
  expiresAt: string;
  resendAvailableAt: string;
}

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isAuthUser = (value: unknown): value is AuthUser => {
  if (!isObjectRecord(value)) {
    return false;
  }

  return typeof value.id === 'string' &&
    typeof value.email === 'string' &&
    (typeof value.name === 'string' || value.name === null);
};

const isAuthTokens = (value: unknown): value is AuthTokens => {
  if (!isObjectRecord(value)) {
    return false;
  }

  return typeof value.accessToken === 'string' &&
    typeof value.refreshToken === 'string';
};

const isLoginVerificationPayload = (value: unknown): value is LoginVerificationPayload => {
  if (!isObjectRecord(value)) {
    return false;
  }

  return typeof value.maskedEmail === 'string' &&
    typeof value.expiresAt === 'string' &&
    typeof value.resendAvailableAt === 'string';
};

const isLoginRedirectResponse = (value: unknown): value is LoginRedirectResponse => {
  if (!isObjectRecord(value)) {
    return false;
  }

  return value.redirectRequired === true &&
    typeof value.targetClientType === 'string';
};

const isLoginChallengeResponse = (value: unknown): value is LoginChallengeResponse => {
  if (!isObjectRecord(value)) {
    return false;
  }

  return value.verificationRequired === true &&
    isAuthUser(value.user) &&
    isLoginVerificationPayload(value.verification);
};

const isLoginSuccessResponse = (value: unknown): value is LoginSuccessResponse => {
  if (!isObjectRecord(value)) {
    return false;
  }

  return value.verificationRequired !== true &&
    isAuthUser(value.user) &&
    isAuthTokens(value.tokens);
};

const PENDING_VERIFICATION_KEY = 'pending-login-verification';

type VerifyTokenMutation = ReturnType<typeof trpc.auth.verifyLoginEmailToken.useMutation>;

function useLoginEmailTokenVerifier(
  location: ReturnType<typeof useLocation>,
  verifyTokenMutation: VerifyTokenMutation,
) {
  // トークンを1度だけ処理するためのRef
  const processedTokenRef = useRef<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('login_verify_token');
    if (!token) {
      return;
    }
    // 同じトークンを既に処理済みの場合はスキップ
    if (processedTokenRef.current === token) {
      return;
    }
    processedTokenRef.current = token;
    verifyTokenMutation.mutate({ token });
    window.history.replaceState({}, '', location.pathname);
  }, [location.pathname, location.search, verifyTokenMutation]);
}

export function LoginPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { setAuth, isAuthenticated, clearAuth } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const messageApi = message;
  const [form] = Form.useForm();
  const [isDemoAccount, setIsDemoAccount] = useState(false);
  // デモアカウントの認証クリアを一度だけ実行するためのRef
  const demoAuthClearedRef = useRef(false);
  // ログイン成功後のナビゲーション先（useRef で同期的に管理）
  const pendingNavigationPathRef = useRef<string | null>(null);
  // ログイン処理中フラグ（clearAuth の誤実行を防ぐ）
  const isLoggingInRef = useRef(false);
  const [pendingVerification, setPendingVerification] = useState<PendingVerificationState | null>(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    try {
      const stored = window.sessionStorage.getItem(PENDING_VERIFICATION_KEY);
      if (!stored) {
        return null;
      }

      const data = JSON.parse(stored) as PendingVerificationState;

      // 有効期限をチェック（期限切れの場合はクリア）
      if (data.expiresAt) {
        const expiresAt = new Date(data.expiresAt);
        if (expiresAt < new Date()) {
          window.sessionStorage.removeItem(PENDING_VERIFICATION_KEY);
          return null;
        }
      }

      return data;
    } catch {
      // パースエラーの場合もクリア
      window.sessionStorage.removeItem(PENDING_VERIFICATION_KEY);
      return null;
    }
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);
  const isDevelopment = import.meta.env.DEV;

  const demoAccount = location.state?.demoAccount as
    | { email: string; password: string }
    | undefined;

  // 既にログイン済みの場合はダッシュボードにリダイレクト
  useEffect(() => {
    // ログイン処理中は何もしない（clearAuth の誤実行を防ぐ）
    // useRef を使用しているため、React state の更新タイミングに依存しない
    if (isLoggingInRef.current) {
      return;
    }

    // メール認証トークンでのアクセス時はリダイレクトしない
    const params = new URLSearchParams(location.search);
    const hasVerificationToken = params.has('login_verify_token');

    // デモアカウントでのログイン時は、既存の認証をクリアしてリダイレクトしない
    const hasDemoAccount = !!location.state?.demoAccount;

    if (isAuthenticated && !hasVerificationToken) {
      if (hasDemoAccount && !demoAuthClearedRef.current) {
        // デモアカウントログイン時: 既存セッションをクリアして新規ログインを許可（一度だけ実行）
        demoAuthClearedRef.current = true;
        clearAuth();
      } else if (!hasDemoAccount) {
        // 通常のログイン済みユーザー: ダッシュボードにリダイレクト
        const from = location.state?.from?.pathname || '/app/dashboard';
        navigate(from, { replace: true });
      }
      // hasDemoAccount && demoAuthClearedRef.current の場合: 何もしない
      // finalizeLogin がダッシュボードへのリダイレクトを処理する
    }
  }, [isAuthenticated, navigate, location.state?.from?.pathname, location.state?.demoAccount, location.search, clearAuth]);

  useEffect(() => {
    if (demoAccount) {
      form.setFieldsValue({
        email: demoAccount.email,
        password: demoAccount.password,
      });
      setIsDemoAccount(true);
      // Alertコンポーネントで表示するため、トースト通知は不要
    }
  }, [demoAccount, form]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (pendingVerification) {
      window.sessionStorage.setItem(PENDING_VERIFICATION_KEY, JSON.stringify(pendingVerification));
    } else {
      window.sessionStorage.removeItem(PENDING_VERIFICATION_KEY);
    }
  }, [pendingVerification]);

  // ログイン成功後、isAuthenticated が true になったらナビゲートする
  // useRef を使用して、React state のバッチ処理タイミングに依存しない
  useEffect(() => {
    const pendingPath = pendingNavigationPathRef.current;

    if (pendingPath && isAuthenticated) {
      // ナビゲーション先をクリアしてから遷移（重複防止）
      pendingNavigationPathRef.current = null;
      isLoggingInRef.current = false;
      navigate(pendingPath, { replace: true, state: {} });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!pendingVerification) {
      setResendCountdown(0);
      return;
    }
    const updateCountdown = () => {
      const target = new Date(pendingVerification.resendAvailableAt).getTime();
      const diff = target - Date.now();
      setResendCountdown(diff > 0 ? Math.ceil(diff / 1000) : 0);
    };
    updateCountdown();
    const timer = window.setInterval(updateCountdown, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, [pendingVerification]);

  const finalizeLogin = (user: AuthUser, tokens: AuthTokens, successMessage = 'ログインに成功しました') => {
    // ログイン処理中フラグを立てる（最初の useEffect で clearAuth が呼ばれるのを防ぐ）
    // useRef なので同期的に設定され、次の useEffect 実行時には必ず true になる
    isLoggingInRef.current = true;

    // 古いキャッシュをクリアして新しい認証情報でリクエストが発行されるようにする
    queryClient.clear();

    // デモアカウントからのログインの場合はダッシュボードへ直接遷移
    // それ以外は元のページまたはダッシュボードへ遷移
    const isFromDemoAccount = !!location.state?.demoAccount;
    const targetPath = isFromDemoAccount
      ? '/app/dashboard'
      : (location.state?.from?.pathname || '/app/dashboard');

    // ナビゲーション先を useRef に設定（同期的に設定される）
    pendingNavigationPathRef.current = targetPath;

    // 認証状態を更新（Zustand state + localStorage）
    setAuth(user, tokens.accessToken, tokens.refreshToken);

    messageApi.success(successMessage);
    setLoading(false);
    setPendingVerification(null);
    setVerificationCode('');
  };

  const loginMutation = trpc.auth.login.useMutation({
    onSuccess: (data) => {
      if (isLoginRedirectResponse(data)) {
        const redirectUrl = buildClientRedirectUrl(data.targetClientType);
        if (redirectUrl) {
          messageApi.info('正しいログイン画面へ移動します。');
          window.location.href = redirectUrl;
          return;
        }
        messageApi.error('ログイン先の判定に失敗しました。');
        setLoading(false);
        return;
      }
      if (isLoginChallengeResponse(data)) {
        setPendingVerification({
          email: data.user.email,
          maskedEmail: data.verification.maskedEmail,
          expiresAt: data.verification.expiresAt,
          resendAvailableAt: data.verification.resendAvailableAt,
        });
        setLoading(false);
        setIsDemoAccount(false);
        messageApi.success('認証コードをメールで送信しました。メールボックスをご確認ください。');
        return;
      }

      if (isLoginSuccessResponse(data)) {
        finalizeLogin(data.user, data.tokens);
        return;
      }

      messageApi.error('ログイン応答が不正です。もう一度お試しください。');
      setLoading(false);
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error
        ? error.message
        : 'ログインに失敗しました';
      messageApi.error(errorMessage);
      const errorDiv = document.createElement('div');
      errorDiv.className = 'login-error-message';
      errorDiv.textContent = 'ログインに失敗しました';
      errorDiv.style.color = 'red';
      errorDiv.style.marginTop = '10px';
      errorDiv.setAttribute('role', 'alert');
      const formElement = document.querySelector('.ant-form');
      const existingError = document.querySelector('.login-error-message');
      if (existingError) {
        existingError.remove();
      }
      if (formElement) {
        formElement.appendChild(errorDiv);
      }
      setLoading(false);
    },
  });

  const verifyCodeMutation = trpc.auth.verifyLoginEmailCode.useMutation({
    onSuccess: (data) => {
      finalizeLogin(data.user, data.tokens, 'メール認証が完了しました');
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : '認証コードの確認に失敗しました';
      messageApi.error(errorMessage);
    },
  });

  const resendVerificationMutation = trpc.auth.resendLoginEmailVerification.useMutation({
    onSuccess: (data) => {
      setPendingVerification({
        email: data.user.email,
        maskedEmail: data.verification.maskedEmail,
        expiresAt: data.verification.expiresAt,
        resendAvailableAt: data.verification.resendAvailableAt,
      });
      messageApi.success('認証メールを再送しました。数分以内に届かない場合は迷惑メールもご確認ください。');
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : '再送リクエストに失敗しました';
      messageApi.error(errorMessage);
    },
  });

  const verifyTokenMutation = trpc.auth.verifyLoginEmailToken.useMutation({
    onSuccess: (data) => {
      finalizeLogin(data.user, data.tokens, 'メール認証が完了しました');
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : '認証リンクが無効か期限切れです';
      messageApi.error(errorMessage);
    },
  });

  useLoginEmailTokenVerifier(location, verifyTokenMutation);

  const onFinish = async (values: LoginForm) => {
    setLoading(true);
    try {
      await loginMutation.mutateAsync({
        ...values,
        clientType: resolveCurrentClientType(),
      });
    } catch (error: unknown) {
      console.error('Login error:', error);
      setLoading(false);
    }
  };

  const onFinishFailed = () => {
    setLoading(false);
  };

  const handleVerificationSubmit = async () => {
    if (!pendingVerification || verificationCode.trim().length !== 6) {
      messageApi.warning('6桁の認証コードを入力してください。');
      return;
    }
    try {
      await verifyCodeMutation.mutateAsync({
        email: pendingVerification.email,
        code: verificationCode.trim(),
        clientType: resolveCurrentClientType(),
      });
    } catch (error) {
      console.error('Verification failed:', error);
    }
  };

  const handleResendVerification = async () => {
    if (!pendingVerification) {
      return;
    }
    try {
      await resendVerificationMutation.mutateAsync({
        email: pendingVerification.email,
        clientType: resolveCurrentClientType(),
      });
    } catch (error) {
      console.error('Resend failed:', error);
    }
  };

  const handleCancelVerification = () => {
    setPendingVerification(null);
    setVerificationCode('');
  };

  const renderLoginForm = () => (
    <>
      {isDemoAccount && (
        <Alert
          message="デモアカウント"
          description="デモアカウント情報を自動入力しました。ログインボタンをクリックしてください。"
          type="info"
          showIcon
          style={{ marginBottom: '16px' }}
        />
      )}

      <Form
        form={form}
        name="login"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        autoComplete="off"
        layout="vertical"
        validateTrigger="onSubmit"
        initialValues={
          isDevelopment
            ? {
                email: 'admin@avs1866.com',
                password: 'AdminPassword123!',
              }
            : {}
        }
      >
        <Form.Item
          name="email"
          rules={[
            { required: true, message: 'メールアドレスを入力してください' },
            { type: 'email', message: '有効なメールアドレスを入力してください' },
          ]}
        >
          <Input
            prefix={<UserOutlined />}
            placeholder="メールアドレス"
            size="large"
            disabled={loading}
          />
        </Form.Item>

        <Form.Item
          name="password"
          rules={[{ required: true, message: 'パスワードを入力してください' }]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="パスワード"
            size="large"
            disabled={loading}
          />
        </Form.Item>

        <div style={{ textAlign: 'right', marginBottom: 16 }}>
          <Link to="/auth/password-reset">
            <Button type="link" style={{ padding: 0 }}>
              パスワードをお忘れですか？
            </Button>
          </Link>
        </div>

        <Form.Item style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            size="large"
            block
          >
            ログイン
          </Button>
        </Form.Item>

        {isDevelopment && (
          <div
            style={{
              padding: 12,
              background: '#f0f2f5',
              borderRadius: 4,
              marginBottom: 16,
            }}
          >
            <Text type="secondary" style={{ fontSize: 12 }}>
              開発環境テストアカウント<br />
              メール: admin@avs1866.com<br />
              パスワード: AdminPassword123!
            </Text>
          </div>
        )}

        <Divider />

        <div style={{ textAlign: 'center' }}>
          <Text type="secondary">
            アカウントをお持ちではありませんか？{' '}
            <Link to="/register">
              <Button type="link" style={{ padding: 0 }}>
                新規登録
              </Button>
            </Link>
          </Text>
        </div>
      </Form>
    </>
  );

  const renderVerificationPanel = () => (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Steps
        size="small"
        current={1}
        items={[
          { title: 'ログイン情報' },
          { title: 'メール認証' },
        ]}
      />
      <Alert
        message="メールを確認してください"
        description={(
          <>
            認証メールを <strong>{pendingVerification?.maskedEmail}</strong> に送信しました。メール内のリンクをクリックするか、6桁コードを入力してください。
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              ※ メール受信には最大3分程度かかる場合があります
            </Text>
          </>
        )}
        type="info"
        showIcon
      />
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Text>認証コード（6桁）</Text>
        <Input
          value={verificationCode}
          onChange={(event) => {
            const next = event.target.value.replace(/\D/g, '').slice(0, 6);
            setVerificationCode(next);
          }}
          maxLength={6}
          size="large"
          inputMode="numeric"
          placeholder="123456"
          disabled={getMutationPendingState(verifyCodeMutation)}
        />
        <Button
          type="primary"
          size="large"
          block
          loading={getMutationPendingState(verifyCodeMutation)}
          onClick={handleVerificationSubmit}
        >
          認証して続行
        </Button>
        <Button
          size="large"
          block
          onClick={handleResendVerification}
          disabled={resendCountdown > 0 || getMutationPendingState(resendVerificationMutation)}
          loading={getMutationPendingState(resendVerificationMutation)}
        >
          {resendCountdown > 0 ? `再送 (${resendCountdown}秒)` : '認証メールを再送'}
        </Button>
        <Button type="link" block onClick={handleCancelVerification}>
          別のアカウントでログインする
        </Button>
        <Text type="secondary">
          有効期限: {pendingVerification ? new Date(pendingVerification.expiresAt).toLocaleString('ja-JP') : '-'}
        </Text>
      </Space>
    </Space>
  );

  return (
    <>
      <SEOHead
        title="ログイン｜一元帳"
        description="一元帳のログインページです。メールアドレスとパスワードでログインし、設備・部品・業者・作業履歴を管理できます。"
      />
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px 16px',
      }}>
        <Card style={{ width: '100%', maxWidth: 400, boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div style={{ textAlign: 'center' }}>
              <Link to="/" style={{ textDecoration: 'none' }}>
                <Title level={2} style={{ marginBottom: 8, color: '#1890ff', cursor: 'pointer' }}>
                  {SERVICE_TITLE}
                </Title>
              </Link>
              <Text type="secondary">設備管理システム</Text>
            </div>

            {getMutationPendingState(verifyTokenMutation) && (
              <Alert
                message="メール認証リンクを確認しています"
                description="数秒お待ちください..."
                type="info"
                showIcon
              />
            )}

            {pendingVerification ? renderVerificationPanel() : renderLoginForm()}
          </Space>
        </Card>
      </div>
    </>
  );
}
